import ExclamationCircleIcon from '@heroicons/react/20/solid/ExclamationCircleIcon.js';
import React from 'react';
import { Label as AriaLabel } from 'react-aria-components';

import { tv } from '../../tv';
import { Size } from '../../types';
import { RequiredMark } from './required-mark';

export { resolveFieldState } from './field-state';

/**
 * The anatomy every ComplyKit field shares (field.md): label, control, then
 * one message line. The controls (TextField, TextArea, and the pickers that
 * follow) and the transitional Field wrapper all render through these
 * pieces, so a field looks and wires the same whatever the control is.
 *
 * Shared by direct import only, never through the public barrel.
 */

export type LabelPosition = 'top' | 'side';

/** Set by Form. A field never takes a label position of its own: the
 * container decides, so one form cannot mix the two. */
export const FormLayoutContext = React.createContext<LabelPosition>('top');

export const fieldStyles = tv({
  slots: {
    root: 'ck:min-w-0 ck:font-sans',
    /* 8px between every part, the rhythm the product's forms already have. */
    column: 'ck:flex ck:min-w-0 ck:flex-col ck:gap-2',
    label: 'ck:text-text-default',
    required: 'ck:text-text-muted',
    message: 'ck:flex ck:min-w-0 ck:items-start ck:gap-2',
    description: 'ck:min-w-0 ck:flex-1 ck:text-xs ck:text-text-muted',
    error: 'ck:flex ck:min-w-0 ck:flex-1 ck:items-start ck:gap-1 ck:text-xs ck:text-error-text',
    /* The glyph centres on the first 16px line, so a wrapped error keeps it
     * beside its opening words. */
    errorIcon: 'ck:flex ck:h-4 ck:shrink-0 ck:items-center',
    errorGlyph: 'ck:size-3',
  },
  variants: {
    labelPosition: {
      top: { root: 'ck:flex ck:flex-col ck:gap-2' },
      /* Two cells of the Form grid: the root dissolves so the label and the
       * control column sit in the grid's own columns. The column names its
       * column, so a field with no visible label still lands beside the
       * label column rather than in it. */
      side: {
        root: 'ck:contents',
        label: 'ck:text-text-muted',
        column: 'ck:col-start-2',
      },
    },
    /* The label steps with the control: 12px at sm and md, as the product's
     * forms already read, 14px beside the 40px control. */
    size: {
      sm: { label: 'ck:text-xs ck:font-semibold' },
      md: { label: 'ck:text-xs ck:font-semibold' },
      lg: { label: 'ck:text-sm ck:font-medium' },
    },
  },
  compoundVariants: [
    /* A side label is a quieter term beside its value, the details-tab
     * convention, so it drops a weight. */
    { labelPosition: 'side', size: ['sm', 'md'], class: { label: 'ck:font-medium' } },
  ],
});

/**
 * The bordered box a single-line or multi-line control types into. Shared by
 * every text-entry control so their edges, states and sizes cannot drift.
 *
 * Transparent at rest, like Checkbox's box, so a field composes on any
 * surface; in dark mode a faint well, where a 1px edge alone is too little to
 * find. The state is a variant driven by the field's props rather than React
 * Aria's data attributes, so the precedence of read-only over disabled over
 * invalid is decided in one place. Hover darkens the edge; focus by any
 * input turns it the accent and washes a soft halo out from it (a text
 * entry's focus is state, not navigation); keyboard focus adds the ring, on
 * the entry's focus alone: a ring on the frame's focus-within would double
 * the ring of an action seated inside it.
 *
 * A picker wears the same frame around a button (`kind="trigger"`): the
 * pointer is the hand, the trigger's own focus lights the edge as an entry's
 * does, and while the popup is open the frame keeps that look (`open`) even
 * though focus has moved into the list, so the field reads as the one being
 * edited.
 */
export const frameStyles = tv({
  base: [
    'ck:flex ck:w-full ck:min-w-0 ck:rounded-3 ck:border ck:bg-transparent',
    'ck:font-sans ck:text-text-default ck:transition-frame',
  ],
  variants: {
    kind: {
      entry: 'ck:cursor-text ck:entry-focus-visible:focus-ring',
      trigger: 'ck:cursor-pointer ck:trigger-focus-visible:focus-ring',
    },
    open: {
      true: '',
      false: '',
    },
    /* A multiple picker's frame holds chips that wrap: it grows from the
     * field's height, one row of chips sitting where a line of text would. */
    chips: {
      true: '',
      false: '',
    },
    /* Empty, the frame keeps the field's start padding, so the placeholder
     * lines up with the text of the fields beside it. */
    hasChips: {
      true: '',
      false: '',
    },
    size: {
      sm: 'ck:gap-1 ck:px-2',
      md: 'ck:gap-2 ck:px-3',
      lg: 'ck:gap-2 ck:px-4',
    },
    multiline: {
      false: 'ck:items-center',
      true: 'ck:items-start',
    },
    /* The action seat pulls the frame's end padding in, so the action's own
     * wash sits one inset from the edge rather than a whole gutter away. */
    hasAction: {
      true: 'ck:pe-1',
      false: '',
    },
    state: {
      default: [
        'ck:border-border-default ck:hover:border-border-active',
        'ck:entry-focus:border-accent-8 ck:entry-focus:focus-halo',
        'ck:trigger-focus:border-accent-8 ck:trigger-focus:focus-halo ck:dark:bg-neutral-a-2',
      ],
      /* The error outranks the focus on the edge; the ring still marks it. */
      invalid: [
        'ck:border-error-border ck:entry-focus:focus-halo-error',
        'ck:trigger-focus:focus-halo-error ck:dark:bg-neutral-a-2',
      ],
      /* Read-only softens rather than washes out: a faint fill and a passive
       * edge on the frame, the value muted (entryStyles), the label and the
       * frame at full contrast so the row still reads as a field. A filled
       * well with dark text read as a heavy legacy box; a frameless value
       * read as a broken row in a column of framed fields. */
      readOnly: [
        'ck:border-border-subtle ck:bg-neutral-a-3 ck:dark:bg-neutral-a-4',
        'ck:entry-focus:border-accent-8 ck:entry-focus:focus-halo',
        'ck:trigger-focus:border-accent-8 ck:trigger-focus:focus-halo',
      ],
      /* Button's disabled wash; the field is out of the tab order. */
      disabled:
        'ck:cursor-not-allowed ck:border-border-default ck:opacity-50 ck:dark:bg-neutral-a-2',
    },
  },
  compoundVariants: [
    { multiline: false, size: 'sm', class: 'ck:h-6' },
    { multiline: false, size: 'md', class: 'ck:h-8' },
    { multiline: false, size: 'lg', class: 'ck:h-10' },
    { multiline: true, size: 'sm', class: 'ck:py-1' },
    { multiline: true, size: ['md', 'lg'], class: 'ck:py-2' },
    /* The open picker holds the focus look while the list has the focus;
     * hover restates the accent edge so moving the pointer over the frame
     * cannot dim it mid-pick. */
    {
      open: true,
      state: 'default',
      class: 'ck:border-accent-8 ck:hover:border-accent-8 ck:focus-halo',
    },
    { open: true, state: 'invalid', class: 'ck:focus-halo-error' },
    /* Nothing opens, so the hand goes: the frame reads as a value. */
    { kind: 'trigger', state: 'readOnly', class: 'ck:cursor-default' },
    /* The frame's height comes from its padding, never from centering in a
     * minimum, so one line and several sit the same distance from the edge. */
    { chips: true, class: 'ck:h-auto ck:items-center' },
    { chips: true, size: 'md', class: 'ck:p-chips ck:pe-3' },
    { chips: true, size: 'lg', class: 'ck:p-chips-lg ck:pe-4' },
    { chips: true, hasChips: false, size: 'md', class: 'ck:ps-3' },
    { chips: true, hasChips: false, size: 'lg', class: 'ck:ps-4' },
    // After the chips' end padding, so a seated chevron still sits one inset from the edge.
    { chips: true, hasAction: true, class: 'ck:pe-1' },
  ],
  defaultVariants: {
    kind: 'entry',
    open: false,
    chips: false,
    hasChips: false,
  },
});

export type FrameState = 'default' | 'invalid' | 'readOnly' | 'disabled';

export function frameState({
  isDisabled,
  isReadOnly,
  isInvalid,
}: {
  isDisabled: boolean;
  isReadOnly: boolean;
  isInvalid: boolean;
}): FrameState {
  if (isReadOnly) return 'readOnly';
  if (isDisabled) return 'disabled';
  return isInvalid ? 'invalid' : 'default';
}

/* The native input or textarea, bare inside the frame. Form elements do not
 * inherit font, so the text size is set here rather than on the frame. */
export const entryStyles = tv({
  base: [
    'ck:min-w-0 ck:flex-1 ck:bg-transparent ck:font-sans ck:text-text-default ck:outline-none',
    'ck:placeholder:text-text-muted ck:disabled:cursor-not-allowed',
  ],
  variants: {
    size: {
      sm: 'ck:text-xs',
      md: 'ck:text-sm',
      lg: 'ck:text-base',
    },
    multiline: {
      /* The entry takes part in baseline alignment and fills the frame, so
       * the frame's baseline is the typed text's whatever sits before it (a
       * glyph would otherwise lend its bottom edge as the baseline, dropping
       * a side label by a few pixels), and the text still centres. */
      false: 'ck:h-full ck:self-baseline',
      /* Grows with its content between the rows the inline style sets. */
      true: 'ck:w-full ck:resize-none ck:field-sizing-content ck:overflow-y-auto',
    },
    /* A search entry drops the browser's own cancel glyph: the clear is the
     * field's action, seated in the frame. */
    search: {
      true: 'ck:entry-search',
      false: '',
    },
    /* The read-only value is muted: still legible and copyable, visibly not
     * for typing. Disabled washes the whole field instead (frameStyles). */
    readOnly: {
      true: 'ck:text-text-muted',
      false: '',
    },
  },
});

export const adornmentStyles = tv({
  slots: {
    icon: 'ck:shrink-0 ck:text-text-muted',
    affix: 'ck:shrink-0 ck:whitespace-nowrap ck:text-text-muted ck:select-none',
  },
  variants: {
    size: {
      sm: { icon: 'ck:size-3', affix: 'ck:text-xs' },
      md: { icon: 'ck:size-4', affix: 'ck:text-sm' },
      lg: { icon: 'ck:size-4', affix: 'ck:text-base' },
    },
  },
});

/** A press on the frame's padding, glyph or affix types into the entry, as
 * on a native field; a press on a seated action is the action's own. */
export function focusEntryOnPress(entry: React.RefObject<HTMLElement | null>) {
  return (e: React.MouseEvent) => {
    const element = entry.current;
    if (!element || e.target === element) return;
    if (e.target instanceof Element && e.target.closest('button')) return;
    e.preventDefault();
    element.focus();
  };
}

/** The visible name. Inside a React Aria field it is RAC's Label, associated
 * with the input by the field's own context; the transitional Field wires a
 * plain label by id. The required mark is hidden from assistive technology
 * because the input announces aria-required; a control whose focusable is a
 * button (Select) has nowhere to put that and asks for the mark to be read. */
export function FieldLabel({
  children,
  isRequired,
  announcesRequired = false,
  className,
  id,
  htmlFor,
}: {
  children: React.ReactNode;
  isRequired: boolean;
  announcesRequired?: boolean;
  className: string;
  id?: string;
  htmlFor?: string;
}) {
  const mark = isRequired ? <RequiredMark announces={announcesRequired} /> : null;
  if (htmlFor) {
    return (
      <label id={id} htmlFor={htmlFor} className={className}>
        {children}
        {mark}
      </label>
    );
  }
  return (
    <AriaLabel className={className}>
      {children}
      {mark}
    </AriaLabel>
  );
}

/** The ids of a field's message line, and which one the control is
 * described by. The field owns them rather than React Aria: RAC wires its
 * description id only from its own props, and re-checks it only when the
 * invalid state moves, so a description arriving after mount would never
 * reach `aria-describedby`. */
export function useFieldMessageIds(description: React.ReactNode, error: string | undefined) {
  const descriptionId = React.useId();
  const errorId = React.useId();
  const describedBy = error ? errorId : description ? descriptionId : undefined;
  return { descriptionId, errorId, describedBy };
}

/**
 * The message line under the control. An error REPLACES the description
 * rather than stacking under it (field.md): an error restates the rule it
 * enforces, so the description would only repeat it. The trailing slot is
 * TextArea's character count, which stays in both states.
 *
 * No live region: the message is announced through aria-describedby when
 * focus reaches the field (forms.md).
 */
export function FieldMessage({
  description,
  error,
  descriptionId,
  errorId,
  trailing,
}: {
  description?: React.ReactNode;
  error?: string;
  descriptionId: string;
  errorId: string;
  trailing?: React.ReactNode;
}) {
  const {
    message,
    description: descriptionClass,
    error: errorClass,
    errorIcon,
    errorGlyph,
  } = fieldStyles();
  let body: React.ReactNode = null;
  if (error) {
    body = (
      <div id={errorId} className={errorClass()}>
        <span className={errorIcon()}>
          <ExclamationCircleIcon aria-hidden="true" className={errorGlyph()} />
        </span>
        <span>{error}</span>
      </div>
    );
  } else if (description) {
    body = (
      <div id={descriptionId} className={descriptionClass()}>
        {description}
      </div>
    );
  }
  if (!body && !trailing) return null;
  return (
    <div className={message()}>
      {body ?? <span className={descriptionClass()} />}
      {trailing}
    </div>
  );
}

/** The input a group's ref stands for: where Tab lands in it, so a form
 * library focusing the field reaches a real input. In a radio group that is
 * the checked radio when there is one; every checkbox is its own stop. */
export function focusTarget(root: HTMLElement | null, { checkedFirst = false } = {}) {
  if (!root) return null;
  const checked = checkedFirst
    ? root.querySelector<HTMLInputElement>('input:checked:not(:disabled)')
    : null;
  return checked ?? root.querySelector<HTMLInputElement>('input:not(:disabled)');
}

/** The layout a field renders in, read from the enclosing Form. */
export function useFieldLayout(size: Size) {
  const labelPosition = React.useContext(FormLayoutContext);
  return { labelPosition, styles: fieldStyles({ labelPosition, size }) };
}
